import { ProcessDraftType, ProcessType } from 'configs/constants/enums/process'
import { cloneDeepWith, isEqual, omit } from 'lodash'
import { IGroupMemberPermissionId } from 'interfaces/group'
import { IProcessWithRelations } from 'interfaces/process'
import { IStepWithRelations } from 'interfaces/step'
import { getValidArray } from './common'

export function toProcedureTypeName(procedureType?: number): keyof typeof ProcessType | undefined {
  if (!procedureType) {
    return undefined
  }
  return ProcessType[procedureType] as keyof typeof ProcessType
}

export function toProcessDraftTypeName(procedureType?: number): keyof typeof ProcessDraftType | undefined {
  if (!procedureType) {
    return undefined
  }
  return ProcessDraftType[procedureType] as keyof typeof ProcessDraftType
}

export function isUserHavePermissionToEditProcess(process: IProcessWithRelations | undefined, isBasicUser: boolean) {
  if (!process) {
    return false
  }
  if (isBasicUser) {
    return getValidArray(process?.currentMemberPermissions).some(
      permission => permission.permissionId === IGroupMemberPermissionId.EDITOR
    )
  }
  return true
}

export function checkForNewUpdateOnCommonSteps(stepsCreatedFromCommon: IStepWithRelations[]) {
  const stepNeedUpdates = getValidArray(stepsCreatedFromCommon).filter(stepCreatedFromCommon => {
    const commonStep = omit<IStepWithRelations>(stepCreatedFromCommon?.originalStep, ['id', 'createdAt', 'updatedAt'])
    const derivedStep = omit<IStepWithRelations>(stepCreatedFromCommon, [
      'id',
      'createdAt',
      'updatedAt',
      'originalStepId',
      'originalStep'
    ])
    const isTextContentDifferent = compareDifferenceForArrayOfRecords(
      commonStep?.textContents ?? [],
      derivedStep?.textContents ?? []
    )
    const isMediaDifferent = compareDifferenceForArrayOfRecords(commonStep?.media ?? [], derivedStep?.media ?? [])

    const relations = ['icon', 'media', 'textContents', 'processId']
    const isStepDifferent = !(
      JSON.stringify(omit(commonStep, relations)) === JSON.stringify(omit(derivedStep, relations))
    )
    return isTextContentDifferent || isMediaDifferent || isStepDifferent
  })

  return stepNeedUpdates
}

function compareDifferenceForArrayOfRecords<T>(basedRecords: T[], derivedRecords: T[]) {
  function removeId(obj: any) {
    if (obj !== null && typeof obj === 'object') {
      delete obj.id
      if (obj.stepId) delete obj.stepId
      if (obj.mediumId) delete obj.mediumId
      if (obj.textContentsId) delete obj.textContentsId
      if (obj.decisionPointId) delete obj.decisionPointId
      if (obj.mediaId) delete obj.mediaId
      if (obj.decisionPointMedias) delete obj.decisionPointMedias // TODO compare later
      Object.values(obj).forEach(value => {
        if (Array.isArray(value)) {
          value.forEach(removeId)
        } else if (typeof value === 'object') {
          removeId(value)
        }
      })
    }
    return obj
  }

  if (basedRecords.length !== derivedRecords.length) {
    return true
  }

  const copyBasedRecords = getValidArray(basedRecords).map(item => cloneDeepWith(item, removeId))
  const copyDerivedRecords = getValidArray(derivedRecords).map(item => cloneDeepWith(item, removeId))

  return !isEqual(copyBasedRecords, copyDerivedRecords)
}
